import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PatientList from './PatientList';
import PatientModal from './PatientModal';
import AppointmentModal from './AppointmentModal';
import Toast from './Toast';
import DoctorList from './DoctorList';
import DoctorForm from './DoctorForm';
import AppointmentList from './AppointmentList';
import SubscriptionPlanForm from './SubscriptionPlanForm';
import SubscriptionPlanList from './SubscriptionPlanList';
import PaymentForm from './PaymentForm';
import PaymentList from './PaymentList';
import RoomManagement from './RoomManagement';
import CalendarView from './CalendarView';
import { clearSuccessMessage } from '../redux/slices/patientSlice';
import '../styles/Dashboard.css';

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const [activeSection, setActiveSection] = useState('patients');
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [selectedAppointmentForEdit, setSelectedAppointmentForEdit] = useState(null);
  const sidebarRef = useRef(null);
  const menuToggleRef = useRef(null);
  const patientSuccessMessage = useSelector((state) => state.patients.successMessage);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        menuToggleRef.current &&
        !menuToggleRef.current.contains(event.target)
      ) {
        setIsSidebarOpen(false);
      }
    };

    if (isSidebarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isSidebarOpen]);

  // Handle window resize to adjust sidebar visibility
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        // Desktop: keep sidebar open
        setIsSidebarOpen(true);
      } else {
        // Mobile: close sidebar
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminId');
    window.location.href = '/admin/login';
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
    setIsSidebarOpen(false);
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'patients':
        return (
          <div className="section">
            <h2>Patient Management</h2>
            <PatientList />
            <div className="button-center-container">
              <button 
                className="add-patient-btn"
                onClick={() => setIsPatientModalOpen(true)}
              >
                + Add Patient
              </button>
            </div>
          </div>
        );
      case 'doctors':
        return (
          <div className="section">
            <h2>Doctor Management</h2>
            <DoctorForm />
            <DoctorList />
          </div>
        );
      case 'appointments':
        return (
          <div className="section">
            <h2>Appointment Management</h2>
            <AppointmentList onEdit={(appointment) => {
              setSelectedAppointmentForEdit(appointment);
              setIsAppointmentModalOpen(true);
            }} currentSection={activeSection} />
            <div className="button-center-container">
              <button 
                className="add-patient-btn"
                onClick={() => {
                  setSelectedAppointmentForEdit(null);
                  setIsAppointmentModalOpen(true);
                }}
              >
                + Schedule Appointment
              </button>
            </div>
          </div>
        );
      case 'calendar':
        return (
          <div className="section">
            <CalendarView currentSection={activeSection} />
          </div>
        );
      case 'rooms':
        return (
          <div className="section">
            <RoomManagement />
          </div>
        );
      case 'subscriptions':
        return (
          <div className="section">
            <h2>Subscription Plans</h2>
            <SubscriptionPlanForm />
            <SubscriptionPlanList />
          </div>
        );
      case 'payments':
        return (
          <div className="section">
            <h2>Payment Management</h2>
            <PaymentForm />
            <PaymentList />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="dashboard">
      <Toast 
        message={patientSuccessMessage} 
        type="success" 
        onClose={() => dispatch(clearSuccessMessage())}
      />
      <PatientModal 
        isOpen={isPatientModalOpen} 
        onClose={() => setIsPatientModalOpen(false)}
      />
      <AppointmentModal 
        isOpen={isAppointmentModalOpen} 
        onClose={() => {
          setIsAppointmentModalOpen(false);
          setSelectedAppointmentForEdit(null);
        }}
        appointment={selectedAppointmentForEdit}
      />
      <div ref={sidebarRef} className={`sidebar ${isSidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <h1 className="sidebar-title">HMS</h1>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-item ${activeSection === 'patients' ? 'active' : ''}`}
            onClick={() => handleSectionChange('patients')}
          >
            <span className="nav-icon">👥</span>
            <span className="nav-text">Patient Management</span>
          </button>

          <button
            className={`nav-item ${activeSection === 'doctors' ? 'active' : ''}`}
            onClick={() => handleSectionChange('doctors')}
          >
            <span className="nav-icon">👨‍⚕️</span>
            <span className="nav-text">Doctor Management</span>
          </button>

          <button
            className={`nav-item ${activeSection === 'appointments' ? 'active' : ''}`}
            onClick={() => handleSectionChange('appointments')}
          >
            <span className="nav-icon">📅</span>
            <span className="nav-text">Appointment Management</span>
          </button>

          <button
            className={`nav-item ${activeSection === 'calendar' ? 'active' : ''}`}
            onClick={() => handleSectionChange('calendar')}
          >
            <span className="nav-icon">📆</span>
            <span className="nav-text">Calendar View</span>
          </button>

          <button
            className={`nav-item ${activeSection === 'rooms' ? 'active' : ''}`}
            onClick={() => handleSectionChange('rooms')}
          >
            <span className="nav-icon">🏥</span>
            <span className="nav-text">Room Management</span>
          </button>

          <button
            className={`nav-item ${activeSection === 'subscriptions' ? 'active' : ''}`}
            onClick={() => handleSectionChange('subscriptions')}
          >
            <span className="nav-icon">💳</span>
            <span className="nav-text">Subscription Plans</span>
          </button>

          <button
            className={`nav-item ${activeSection === 'payments' ? 'active' : ''}`}
            onClick={() => handleSectionChange('payments')}
          >
            <span className="nav-icon">💰</span>
            <span className="nav-text">Payment Management</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <button className="logout-btn" onClick={handleLogout}>
            <span className="nav-icon">🚪</span>
            <span className="nav-text">Logout</span>
          </button>
        </div>
      </div>

      <div className="main-content">
        <header className="top-header">
          <div className="header-left">
            <button
              ref={menuToggleRef}
              className="menu-toggle"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            >
              {isSidebarOpen ? '✕' : '☰'}
            </button>
            <h1>Healthcare Management System - Admin</h1>
          </div>
          <div className="header-right">
            <button className="logout-button" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </header>

        <div className="content-area">{renderContent()}</div>
      </div>
    </div>
  );
};

export default AdminDashboard;
